import { ExchangeCode, TokenAvailability } from "@/lib/market-data/market-data.types";
import { isChartableAvailability } from "./candle-symbols";
import {
  CANDLE_INTERVAL_MS,
  CandleFetchParams,
  CandleInterval,
  CandlePoint,
  getExchangeCandlePageSize,
} from "./candle.types";

export type HistoricalCandleResolution =
  | {
      status: "candles";
      exchange: ExchangeCode;
      candles: CandlePoint[];
    }
  | { status: "unavailable" }
  | { status: "rate_limited" };

export interface ResolveHistoricalCandlesParams {
  symbol: string;
  candidates: ExchangeCode[];
  interval: CandleInterval;
  startTime: number;
  endTime: number;
  userId?: string;
  pageLimit?: number;
  /** When every listing window is empty, load the latest candles instead. */
  allowRecentFallback?: boolean;
  /** Candidates were already chosen. Skip the live availability lookup. */
  trustCandidates?: boolean;
  checkAvailable: (
    exchange: ExchangeCode,
    symbol: string
  ) => Promise<TokenAvailability>;
  fetchCandles: (
    exchange: ExchangeCode,
    params: CandleFetchParams
  ) => Promise<CandlePoint[]>;
}

function isRateLimitError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : "";
  const lower = message.toLowerCase();
  return (
    lower.includes("429") ||
    lower.includes("rate limit") ||
    lower.includes("too many")
  );
}

async function loadCandles(
  exchange: ExchangeCode,
  startTime: number,
  endTime: number,
  params: ResolveHistoricalCandlesParams
): Promise<CandlePoint[] | "rate_limited"> {
  try {
    return await params.fetchCandles(exchange, {
      symbol: params.symbol,
      interval: params.interval,
      startTime,
      endTime,
      limit: getExchangeCandlePageSize(exchange, params.pageLimit),
      userId: params.userId,
    });
  } catch (error) {
    if (isRateLimitError(error)) return "rate_limited";
    return [];
  }
}

async function loadPhase(
  exchanges: ExchangeCode[],
  startFor: (exchange: ExchangeCode) => number,
  endFor: (exchange: ExchangeCode) => number,
  params: ResolveHistoricalCandlesParams
): Promise<HistoricalCandleResolution | "empty"> {
  const loaded = await Promise.all(
    exchanges.map(async (exchange) => ({
      exchange,
      candles: await loadCandles(
        exchange,
        startFor(exchange),
        endFor(exchange),
        params
      ),
    }))
  );

  let sawRateLimit = false;
  for (const exchange of exchanges) {
    const result = loaded.find((item) => item.exchange === exchange);
    if (!result || result.candles === "rate_limited") {
      sawRateLimit = sawRateLimit || result?.candles === "rate_limited";
      continue;
    }
    const windowStart = startFor(exchange);
    const windowEnd = endFor(exchange);
    const intervalMs = CANDLE_INTERVAL_MS[params.interval];
    const inWindow = result.candles.filter((candle) => {
      const candleEnd = candle.startTime + intervalMs;
      return candleEnd > windowStart && candle.startTime < windowEnd;
    });
    if (inWindow.length > 0) {
      return { status: "candles", exchange, candles: inWindow };
    }
  }

  if (sawRateLimit) return { status: "rate_limited" };
  return "empty";
}

/**
 * Ask every chartable exchange for the alert window at once. A Binance miss
 * does not block Aster, Gate, or KuCoin. Latest candles are used only after
 * every listing window is empty.
 */
export async function resolveHistoricalCandles(
  params: ResolveHistoricalCandlesParams
): Promise<HistoricalCandleResolution> {
  const chartable: ExchangeCode[] = [];

  for (const candidate of params.candidates) {
    if (params.trustCandidates) {
      chartable.push(candidate);
      continue;
    }
    try {
      const availability = await params.checkAvailable(candidate, params.symbol);
      if (!isChartableAvailability(availability)) continue;
      chartable.push(candidate);
    } catch (error) {
      if (isRateLimitError(error)) return { status: "rate_limited" };
    }
  }

  const listingWindow = await loadPhase(
    chartable,
    () => params.startTime,
    () => params.endTime,
    params
  );
  if (listingWindow !== "empty") return listingWindow;

  if (params.allowRecentFallback) {
    const recentWindow = await loadPhase(
      chartable,
      (exchange) => {
        const limit = getExchangeCandlePageSize(exchange, params.pageLimit);
        return Date.now() - CANDLE_INTERVAL_MS[params.interval] * limit;
      },
      () => Date.now(),
      params
    );
    if (recentWindow !== "empty") return recentWindow;
  }

  return { status: "unavailable" };
}
